import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Download, FileSpreadsheet } from 'lucide-react';
import { api, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { useBaseCurrency, useGstEnabled } from '@/hooks/useOrgSettings';
import { PageHeader } from '@/app/DashboardLayout';
import { formatDate, lastMonth, type Period } from '@/lib/dates';
import {
  Badge, Button, Card, EmptyState, ErrorState, Money, PeriodPicker, SkeletonTable,
  TBody, TD, TH, THead, TR, Table,
} from '@/features/common/ui';

interface TaxAmounts {
  taxableValue: string;
  igst: string;
  cgst: string;
  sgst: string;
  cess: string;
}

interface B2bRow extends TaxAmounts {
  gstin: string;
  customerName: string;
  invoiceNumber: string;
  invoiceDate: string;
  invoiceValue: string;
  placeOfSupply: string;
  reverseCharge: boolean;
  rate: number;
  documentId: string;
}

interface B2clRow extends TaxAmounts {
  customerName: string;
  invoiceNumber: string;
  invoiceDate: string;
  invoiceValue: string;
  placeOfSupply: string;
  rate: number;
  documentId: string;
}

interface ExportRow extends TaxAmounts {
  customerName: string;
  invoiceNumber: string;
  invoiceDate: string;
  invoiceValue: string;
  exportType: string;
  rate: number;
  documentId: string;
}

interface B2csRow extends TaxAmounts {
  placeOfSupply: string;
  rate: number;
  supplyType: 'INTRA' | 'INTER';
  invoiceCount: number;
}

interface HsnRow extends TaxAmounts {
  hsnSac: string;
  description: string;
  uqc: string;
  quantity: string;
  totalValue: string;
  rate: number;
  segment: 'B2B' | 'B2C';
}

interface Gstr1 {
  period: { from: string; to: string };
  gstin: string | null;
  table4aB2b: B2bRow[];
  table5B2cl: B2clRow[];
  table6aExports: ExportRow[];
  table7B2cs: B2csRow[];
  table8NilExempt: Array<Record<string, string>>;
  table12Hsn: HsnRow[];
  table13DocumentSeries: Array<{
    natureOfDocument: string;
    fromSerial: string;
    toSerial: string;
    totalIssued: number;
    cancelled: number;
    netIssued: number;
  }>;
  totals: TaxAmounts & { invoiceCount: number };
  zeroRatedTotal: TaxAmounts;
  nilExemptTotal: TaxAmounts;
  warnings: string[];
}

interface Gstr3b {
  period: { from: string; to: string };
  gstin: string | null;
  outwardSupplies: {
    taxableOutward: TaxAmounts;
    zeroRated: TaxAmounts;
    nilRatedExempt: TaxAmounts;
    inwardReverseCharge: TaxAmounts;
    nonGst: TaxAmounts;
  };
  interStateUnregistered: Array<{ placeOfSupply: string; taxableValue: string; igst: string }>;
  eligibleItc: { allOtherItc: TaxAmounts; net: TaxAmounts };
  warnings: string[];
}

type Tab = 'gstr1' | 'gstr3b';

/**
 * The returns, on screen, with every figure traceable to the invoices behind
 * it.
 *
 * ── THE RECONCILIATION IS THE HEADLINE ──
 * The first thing a CA does with these two is check that GSTR-3B 3.1(a) equals
 * GSTR-1's 4A + 5 + 7. So that comparison is the first thing on the page,
 * rather than something they have to work out by flipping between tabs.
 *
 * ── WARNINGS ARE ABOVE THE NUMBERS, NOT BELOW THEM ──
 * A warning under a table is a warning read after the figure has already been
 * copied.
 */
export function GstReturnsPage() {
  const org = useActiveOrg();
  const gstEnabled = useGstEnabled();
  const [period, setPeriod] = useState<Period>(() => lastMonth());
  const [tab, setTab] = useState<Tab>('gstr1');

  const gstr1 = useQuery({
    queryKey: ['gstr1', org?.id, period.from, period.to],
    queryFn: () => api.get<Gstr1>(`/gst/gstr1${qs({ from: period.from, to: period.to })}`),
    enabled: !!org?.id && gstEnabled,
  });

  const gstr3b = useQuery({
    queryKey: ['gstr3b', org?.id, period.from, period.to],
    queryFn: () => api.get<Gstr3b>(`/gst/gstr3b${qs({ from: period.from, to: period.to })}`),
    enabled: !!org?.id && gstEnabled && tab === 'gstr3b',
  });

  if (!gstEnabled) {
    return (
      <>
        <PageHeader title="GST returns" />
        <div className="px-5 pb-8">
          <Card>
            <EmptyState
              icon={FileSpreadsheet}
              title="GST is not switched on"
              description="Turn on the GST regime in settings and pick the state you are registered in. Everything on this page is built from invoices raised after that."
              action={
                <Button size="sm" asChild>
                  <Link to="/settings">Open settings</Link>
                </Button>
              }
            />
          </Card>
        </div>
      </>
    );
  }

  const active = tab === 'gstr1' ? gstr1 : gstr3b;
  const warnings = (tab === 'gstr1' ? gstr1.data?.warnings : gstr3b.data?.warnings) ?? [];

  return (
    <>
      <PageHeader
        title="GST returns"
        subtitle={gstr1.data?.gstin ? `GSTIN ${gstr1.data.gstin}` : 'No GSTIN set for this business.'}
        actions={
          <Button size="sm" variant="outline" asChild>
            {/* A plain link, not a fetch: the browser's own download handles the
                file, the filename and the progress bar. The org is in the PATH
                because a browser navigation cannot carry the x-org-id header
                every other request uses. */}
            <a href={`/v1/gst/${org?.id}/${tab}.xlsx${qs({ from: period.from, to: period.to })}`} download>
              <Download className="size-4" aria-hidden />
              Excel
            </a>
          </Button>
        }
      />

      <div className="space-y-4 px-5 pb-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <PeriodPicker value={period} onChange={setPeriod} />
          <div className="flex gap-1">
            {(['gstr1', 'gstr3b'] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setTab(value)}
                aria-pressed={tab === value}
                className={
                  tab === value
                    ? 'rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary'
                    : 'rounded-full px-3 py-1 text-xs text-muted-foreground hover:bg-accent'
                }
              >
                {value === 'gstr1' ? 'GSTR-1' : 'GSTR-3B'}
              </button>
            ))}
          </div>
        </div>

        {warnings.length ? (
          <Card className="border-warning/40 bg-warning/5 p-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-warning">
              <AlertTriangle className="size-4" aria-hidden />
              Read before filing
            </h2>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </Card>
        ) : null}

        {active.isError ? (
          <Card>
            <ErrorState error={active.error} onRetry={() => active.refetch()} />
          </Card>
        ) : active.isLoading ? (
          <Card>
            <SkeletonTable rows={8} cols={6} />
          </Card>
        ) : tab === 'gstr1' && gstr1.data ? (
          <Gstr1View data={gstr1.data} period={period} />
        ) : tab === 'gstr3b' && gstr3b.data && gstr1.data ? (
          <Gstr3bView data={gstr3b.data} gstr1={gstr1.data} />
        ) : null}
      </div>
    </>
  );
}

function Gstr1View({ data, period }: { data: Gstr1; period: Period }) {
  const currency = useBaseCurrency();
  return (
    <>
      <Card className="p-4">
        <h2 className="text-sm font-semibold">
          Summary for {formatDate(period.from)} to {formatDate(period.to)}
        </h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <Figure
            label="Taxable outward supplies"
            hint="4A + 5 + 7. This is what GSTR-3B 3.1(a) must equal."
            value={data.totals.taxableValue}
            sub={`${data.totals.invoiceCount} invoice${data.totals.invoiceCount === 1 ? '' : 's'}`}
          />
          <Figure label="Zero rated" hint="Table 6A. Reported in 3.1(b)." value={data.zeroRatedTotal.taxableValue} />
          <Figure label="Nil rated and exempt" hint="Table 8. Reported in 3.1(c)." value={data.nilExemptTotal.taxableValue} />
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <Figure label="IGST" value={data.totals.igst} />
          <Figure label="CGST" value={data.totals.cgst} />
          <Figure label="SGST / UTGST" value={data.totals.sgst} />
        </div>
      </Card>

      <TableCard
        title="4A — B2B invoices"
        note="Invoice-wise supplies to registered persons. Click a number to open the invoice."
        empty="No supplies to registered customers in this period."
        rows={data.table4aB2b}
        columns={[
          { head: 'Invoice', render: (r) => <InvoiceLink id={r.documentId} number={r.invoiceNumber} /> },
          { head: 'Date', render: (r) => formatDate(r.invoiceDate) },
          { head: 'Customer', render: (r) => r.customerName },
          { head: 'GSTIN', render: (r) => <span className="font-mono text-xs">{r.gstin}</span> },
          { head: 'POS', render: (r) => r.placeOfSupply },
          { head: 'Rate', render: (r) => `${r.rate}%`, numeric: true },
          { head: 'Taxable', render: (r) => <Money value={r.taxableValue} currency={currency} />, numeric: true },
          { head: 'IGST', render: (r) => <Money value={r.igst} currency={currency} />, numeric: true },
          { head: 'CGST', render: (r) => <Money value={r.cgst} currency={currency} />, numeric: true },
          { head: 'SGST', render: (r) => <Money value={r.sgst} currency={currency} />, numeric: true },
        ]}
      />

      <TableCard
        title="5 — B2CL"
        note="Inter-state supplies to unregistered persons above the notified threshold, invoice-wise."
        empty="Nothing above the threshold this period."
        rows={data.table5B2cl}
        columns={[
          { head: 'Invoice', render: (r) => <InvoiceLink id={r.documentId} number={r.invoiceNumber} /> },
          { head: 'Date', render: (r) => formatDate(r.invoiceDate) },
          { head: 'Customer', render: (r) => r.customerName },
          { head: 'POS', render: (r) => r.placeOfSupply },
          { head: 'Rate', render: (r) => `${r.rate}%`, numeric: true },
          { head: 'Taxable', render: (r) => <Money value={r.taxableValue} currency={currency} />, numeric: true },
          { head: 'IGST', render: (r) => <Money value={r.igst} currency={currency} />, numeric: true },
        ]}
      />

      <TableCard
        title="6A — Exports and zero rated"
        note="Exports, SEZ and deemed exports. Not counted in 4A, 5 or 7."
        empty="No zero-rated supplies this period."
        rows={data.table6aExports}
        columns={[
          { head: 'Invoice', render: (r) => <InvoiceLink id={r.documentId} number={r.invoiceNumber} /> },
          { head: 'Date', render: (r) => formatDate(r.invoiceDate) },
          { head: 'Customer', render: (r) => r.customerName },
          { head: 'Type', render: (r) => <Badge variant="neutral">{r.exportType}</Badge> },
          { head: 'Taxable', render: (r) => <Money value={r.taxableValue} currency={currency} />, numeric: true },
          { head: 'IGST', render: (r) => <Money value={r.igst} currency={currency} />, numeric: true },
        ]}
      />

      <TableCard
        title="7 — B2CS"
        note="Everything else unregistered, summarised by place of supply and rate."
        empty="No B2C supplies this period."
        rows={data.table7B2cs}
        columns={[
          { head: 'Type', render: (r) => r.supplyType },
          { head: 'POS', render: (r) => r.placeOfSupply },
          { head: 'Rate', render: (r) => `${r.rate}%`, numeric: true },
          { head: 'Invoices', render: (r) => r.invoiceCount, numeric: true },
          { head: 'Taxable', render: (r) => <Money value={r.taxableValue} currency={currency} />, numeric: true },
          { head: 'IGST', render: (r) => <Money value={r.igst} currency={currency} />, numeric: true },
          { head: 'CGST', render: (r) => <Money value={r.cgst} currency={currency} />, numeric: true },
          { head: 'SGST', render: (r) => <Money value={r.sgst} currency={currency} />, numeric: true },
        ]}
      />

      <TableCard
        title="12 — HSN summary"
        note="Split into B2B and B2C, which the portal has required since May 2025."
        empty="No lines to summarise."
        rows={data.table12Hsn}
        columns={[
          { head: 'Segment', render: (r) => <Badge variant={r.segment === 'B2B' ? 'info' : 'neutral'}>{r.segment}</Badge> },
          { head: 'HSN / SAC', render: (r) => <span className="font-mono text-xs">{r.hsnSac || '— missing —'}</span> },
          { head: 'Description', render: (r) => r.description },
          { head: 'UQC', render: (r) => r.uqc },
          { head: 'Qty', render: (r) => r.quantity, numeric: true },
          { head: 'Rate', render: (r) => `${r.rate}%`, numeric: true },
          { head: 'Taxable', render: (r) => <Money value={r.taxableValue} currency={currency} />, numeric: true },
          { head: 'Total value', render: (r) => <Money value={r.totalValue} currency={currency} />, numeric: true },
        ]}
      />

      <TableCard
        title="13 — Documents issued"
        note="The serial range issued in this period. Cancelled counts the invoices that were voided - which is why a voided invoice keeps its number."
        empty="No documents issued in this period."
        rows={data.table13DocumentSeries}
        columns={[
          { head: 'Nature', render: (r) => r.natureOfDocument },
          { head: 'From', render: (r) => r.fromSerial },
          { head: 'To', render: (r) => r.toSerial },
          { head: 'Total', render: (r) => r.totalIssued, numeric: true },
          { head: 'Cancelled', render: (r) => r.cancelled, numeric: true },
          { head: 'Net issued', render: (r) => r.netIssued, numeric: true },
        ]}
      />
    </>
  );
}

function Gstr3bView({ data, gstr1 }: { data: Gstr3b; gstr1: Gstr1 }) {
  const currency = useBaseCurrency();
  const ties = data.outwardSupplies.taxableOutward.taxableValue === gstr1.totals.taxableValue;

  return (
    <>
      <Card className={ties ? 'border-success/40 bg-success/5 p-4' : 'border-destructive/40 bg-destructive/5 p-4'}>
        <h2 className="text-sm font-semibold">
          {ties ? '3.1(a) ties to GSTR-1' : '3.1(a) does NOT tie to GSTR-1'}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          3.1(a) is <Money value={data.outwardSupplies.taxableOutward.taxableValue} currency={currency} className="font-medium text-foreground" />
          {' · '}
          GSTR-1 4A + 5 + 7 is <Money value={gstr1.totals.taxableValue} currency={currency} className="font-medium text-foreground" />
        </p>
      </Card>

      <Card className="overflow-x-auto">
        <div className="p-4 pb-0">
          <h2 className="text-sm font-semibold">3.1 Outward supplies</h2>
        </div>
        <Table>
          <THead>
            <TR>
              <TH>Nature of supply</TH>
              <TH numeric>Taxable value</TH>
              <TH numeric>IGST</TH>
              <TH numeric>CGST</TH>
              <TH numeric>SGST</TH>
            </TR>
          </THead>
          <TBody>
            {(
              [
                ['(a) Outward taxable (other than zero rated, nil rated, exempted)', data.outwardSupplies.taxableOutward],
                ['(b) Outward taxable (zero rated)', data.outwardSupplies.zeroRated],
                ['(c) Other outward (nil rated, exempted)', data.outwardSupplies.nilRatedExempt],
                ['(d) Inward liable to reverse charge', data.outwardSupplies.inwardReverseCharge],
                ['(e) Non-GST outward supplies', data.outwardSupplies.nonGst],
              ] as const
            ).map(([label, amounts]) => (
              <TR key={label}>
                <TD>{label}</TD>
                <TD numeric>
                  <Money value={amounts.taxableValue} currency={currency} />
                </TD>
                <TD numeric>
                  <Money value={amounts.igst} currency={currency} />
                </TD>
                <TD numeric>
                  <Money value={amounts.cgst} currency={currency} />
                </TD>
                <TD numeric>
                  <Money value={amounts.sgst} currency={currency} />
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </Card>

      <TableCard
        title="3.2 — Inter-state supplies to unregistered persons"
        note="Of the figure in 3.1(a)."
        empty="No inter-state B2C supplies this period."
        rows={data.interStateUnregistered}
        columns={[
          { head: 'Place of supply', render: (r) => r.placeOfSupply },
          { head: 'Taxable value', render: (r) => <Money value={r.taxableValue} currency={currency} />, numeric: true },
          { head: 'IGST', render: (r) => <Money value={r.igst} currency={currency} />, numeric: true },
        ]}
      />

      <Card className="p-4">
        <h2 className="text-sm font-semibold">4 — Eligible ITC</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          All other ITC: <Money value={data.eligibleItc.allOtherItc.igst} currency={currency} className="font-medium text-foreground" />
        </p>
      </Card>
    </>
  );
}

function Figure({ label, value, hint, sub }: { label: string; value: string; hint?: string; sub?: string }) {
  const currency = useBaseCurrency();
  return (
    <div className="rounded-md border border-border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums">
        <Money value={value} currency={currency} />
      </p>
      {sub ? <p className="text-xs text-muted-foreground">{sub}</p> : null}
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function InvoiceLink({ id, number }: { id: string; number: string }) {
  return (
    <Link to={`/invoices/${id}`} className="font-medium text-primary hover:underline">
      {number}
    </Link>
  );
}

interface Column<T> {
  head: string;
  render: (row: T) => React.ReactNode;
  numeric?: boolean;
}

function TableCard<T>({
  title,
  note,
  empty,
  rows,
  columns,
}: {
  title: string;
  note: string;
  empty: string;
  rows: T[];
  columns: Column<T>[];
}) {
  const [open, setOpen] = useState(true);
  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((on) => !on)}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-3 p-4 text-left hover:bg-accent/50"
      >
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">{title}</h2>
          <p className="text-xs text-muted-foreground">{note}</p>
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">
          {rows.length} {rows.length === 1 ? 'row' : 'rows'}
        </span>
      </button>

      {open ? (
        rows.length ? (
          <div className="overflow-x-auto border-t border-border">
            <Table>
              <THead>
                <TR>
                  {columns.map((column) => (
                    <TH key={column.head} numeric={column.numeric}>
                      {column.head}
                    </TH>
                  ))}
                </TR>
              </THead>
              <TBody>
                {rows.map((row, index) => (
                  <TR key={index}>
                    {columns.map((column) => (
                      <TD key={column.head} numeric={column.numeric}>
                        {column.render(row)}
                      </TD>
                    ))}
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        ) : (
          <p className="border-t border-border px-4 py-6 text-center text-sm text-muted-foreground">{empty}</p>
        )
      ) : null}
    </Card>
  );
}
